import {
  FuelImpact,
  NullablePlanComponent,
  PlanComponentCategory,
  EfficiencyResultsByFuel,
  ElectrificationResultsByFuel,
} from "@labs/shared/types/AnalyzedPlan.ts";
import { FuelSource } from "@labs/shared/types/FuelSource.ts";
import {
  CostScore,
  SolutionMap,
  SolutionOptionKey,
} from "@labs/shared/types/Solution.ts";
import { getSolutionValueDecimal } from "./getSolutionValueDecimal.ts";

const getFuelImpactFromComponent = (
  component: NullablePlanComponent,
  fuelSource: FuelSource,
): FuelImpact =>
  component?.fuelImpacts?.find(
    (impact) => impact.fuelSource === fuelSource,
  ) || { fuelSource, carbonImpact: 0 };

export const getBuyCleanComponent = (
  solutionMap: SolutionMap,
  stationaryElectrificationComponent: NullablePlanComponent,
  mobileElectrificationComponent: NullablePlanComponent,
  efficiencyResultsByFuel: EfficiencyResultsByFuel,
  electrificationResultsByFuel: ElectrificationResultsByFuel,
) => {
  // start buy clean
  const fuelSwitchingNaturalGas = getSolutionValueDecimal(
    solutionMap,
    SolutionOptionKey.LowNoCarbonFuelSwitchingNaturalGas,
  );
  const fuelSwitchingPropane = getSolutionValueDecimal(
    solutionMap,
    SolutionOptionKey.LowNoCarbonFuelSwitchingPropane,
  );
  const fuelSwitchingFuelOil = getSolutionValueDecimal(
    solutionMap,
    SolutionOptionKey.LowNoCarbonFuelSwitchingFuelOil,
  );
  const fuelSwitchingDiesel = getSolutionValueDecimal(
    solutionMap,
    SolutionOptionKey.LowNoCarbonFuelSwitchingDiesel,
  );
  const fuelSwitchingGasoline = getSolutionValueDecimal(
    solutionMap,
    SolutionOptionKey.LowNoCarbonFuelSwitchingGasoline,
  );

  const coGen = getSolutionValueDecimal(
    solutionMap,
    SolutionOptionKey.CoGenerationElectricity,
  );
  const cleanPowerPurchase = getSolutionValueDecimal(
    solutionMap,
    SolutionOptionKey.RenewablePurchaseFromUtilityElectricity,
  );
  const cleanPPA = getSolutionValueDecimal(
    solutionMap,
    SolutionOptionKey.RenewableEnergyAsPpaVppaElectricity,
  );
  const recs = getSolutionValueDecimal(
    solutionMap,
    SolutionOptionKey.RenewableCreditsRecsElectricity,
  );
  const onsiteGeneration = getSolutionValueDecimal(
    solutionMap,
    SolutionOptionKey.OnsiteSolarOrRenewableEnergyGenerationElectricity,
  );

  const stationaryElectrificationImpactCarbon = getFuelImpactFromComponent(
    stationaryElectrificationComponent,
    FuelSource.Electricity,
  ).carbonImpact;
  const mobileElectrificationImpactCarbon = getFuelImpactFromComponent(
    mobileElectrificationComponent,
    FuelSource.Electricity,
  ).carbonImpact;

  const electricityUnitsOfCarbonAfterIntensityReductionAndElectrification =
    efficiencyResultsByFuel.electricity.unitsOfCarbonAfterReduction +
    (stationaryElectrificationImpactCarbon || 0) +
    (mobileElectrificationImpactCarbon || 0);

  const carbonNaturalGasAfterFuelSwitching =
    electrificationResultsByFuel.naturalGas.unitsOfCarbonAfterReduction *
    (1 - fuelSwitchingNaturalGas);
  const carbonPropaneAfterFuelSwitching =
    electrificationResultsByFuel.propane.unitsOfCarbonAfterReduction *
    (1 - fuelSwitchingPropane);
  const carbonFuelOilAfterFuelSwitching =
    electrificationResultsByFuel.fuelOil.unitsOfCarbonAfterReduction *
    (1 - fuelSwitchingFuelOil);
  const carbonDieselAfterFuelSwitching =
    electrificationResultsByFuel.diesel.unitsOfCarbonAfterReduction *
    (1 - fuelSwitchingDiesel);
  const carbonGasolineAfterFuelSwitching =
    electrificationResultsByFuel.gasoline.unitsOfCarbonAfterReduction *
    (1 - fuelSwitchingGasoline);

  const carbonImpactNaturalGasFuelSwitching =
    carbonNaturalGasAfterFuelSwitching -
    electrificationResultsByFuel.naturalGas.unitsOfCarbonAfterReduction;
  const carbonImpactPropaneFuelSwitching =
    carbonPropaneAfterFuelSwitching -
    electrificationResultsByFuel.propane.unitsOfCarbonAfterReduction;
  const carbonImpactFuelOilFuelSwitching =
    carbonFuelOilAfterFuelSwitching -
    electrificationResultsByFuel.fuelOil.unitsOfCarbonAfterReduction;
  const carbonImpactDieselFuelSwitching =
    carbonDieselAfterFuelSwitching -
    electrificationResultsByFuel.diesel.unitsOfCarbonAfterReduction;
  const carbonImpactGasolineFuelSwitching =
    carbonGasolineAfterFuelSwitching -
    electrificationResultsByFuel.gasoline.unitsOfCarbonAfterReduction;

  const fuelSwitchingCarbonImpact =
    carbonImpactNaturalGasFuelSwitching +
    carbonImpactPropaneFuelSwitching +
    carbonImpactFuelOilFuelSwitching +
    carbonImpactDieselFuelSwitching +
    carbonImpactGasolineFuelSwitching;

  const percentOfPurchasedElectricityClean =
    coGen + cleanPowerPurchase + cleanPPA + recs + onsiteGeneration;
  const electricityUnitsOfCarbonAfterCleanPowerPurchase =
    electricityUnitsOfCarbonAfterIntensityReductionAndElectrification *
    (1 - percentOfPurchasedElectricityClean);

  const buyCleanCarbonImpact =
    electricityUnitsOfCarbonAfterCleanPowerPurchase -
    electricityUnitsOfCarbonAfterIntensityReductionAndElectrification;

  const fuelSwitchingNaturalGasCostScore =
    -carbonImpactNaturalGasFuelSwitching * CostScore.Four;
  const fuelSwitchingPropaneCostScore =
    -carbonImpactPropaneFuelSwitching * CostScore.Four;
  const fuelSwitchingFuelOilCostScore =
    -carbonImpactFuelOilFuelSwitching * CostScore.Four;
  const fuelSwitchingGasolineCostScore =
    -carbonImpactGasolineFuelSwitching * CostScore.Four;
  const fuelSwitchingDieselCostScore =
    -carbonImpactDieselFuelSwitching * CostScore.Two;
  const fuelSwitchingCostScore =
    fuelSwitchingNaturalGasCostScore +
    fuelSwitchingPropaneCostScore +
    fuelSwitchingFuelOilCostScore +
    fuelSwitchingGasolineCostScore +
    fuelSwitchingDieselCostScore;

  const carbonImpactCoGen =
    electricityUnitsOfCarbonAfterIntensityReductionAndElectrification * -coGen;
  const carbonImpactCleanPowerPurchase =
    electricityUnitsOfCarbonAfterIntensityReductionAndElectrification *
    -cleanPowerPurchase;
  const carbonImpactCleanPPA =
    electricityUnitsOfCarbonAfterIntensityReductionAndElectrification *
    -cleanPPA;
  const carbonImpactRECs =
    electricityUnitsOfCarbonAfterIntensityReductionAndElectrification * -recs;
  const carbonImpactOnsiteGeneration =
    electricityUnitsOfCarbonAfterIntensityReductionAndElectrification *
    -onsiteGeneration;

  const coGenCostScore = -carbonImpactCoGen * CostScore.Four;
  const cleanPowerPurchaseCostScore =
    -carbonImpactCleanPowerPurchase * CostScore.Three;
  const cleanPPACostScore = -carbonImpactCleanPPA * CostScore.Three;
  const recsCostScore = -carbonImpactRECs * CostScore.Three;
  const onsiteGenerationCostScore =
    -carbonImpactOnsiteGeneration * CostScore.Two;

  const cleanPurchaseCostScore =
    coGenCostScore +
    cleanPowerPurchaseCostScore +
    cleanPPACostScore +
    recsCostScore +
    onsiteGenerationCostScore;

  const buyCleanCostScore = cleanPurchaseCostScore + fuelSwitchingCostScore;

  const fuelSwitchingSolutionSelected =
    fuelSwitchingNaturalGas > 0 ||
    fuelSwitchingPropane > 0 ||
    fuelSwitchingFuelOil > 0 ||
    fuelSwitchingDiesel > 0 ||
    fuelSwitchingGasoline > 0;

  const buyCleanComponent: NullablePlanComponent =
    percentOfPurchasedElectricityClean > 0 || fuelSwitchingSolutionSelected
      ? {
          planComponentCategory: PlanComponentCategory.BuyClean,
          intermediateCostScore: buyCleanCostScore,
          carbonImpact: buyCleanCarbonImpact + fuelSwitchingCarbonImpact,
          fuelImpacts: [
            ...(percentOfPurchasedElectricityClean > 0
              ? [
                  {
                    fuelSource: FuelSource.Electricity,
                    carbonImpact: buyCleanCarbonImpact,
                  },
                ]
              : []),
            ...(fuelSwitchingNaturalGas > 0
              ? [
                  {
                    fuelSource: FuelSource.NaturalGas,
                    carbonImpact: carbonImpactNaturalGasFuelSwitching,
                  },
                ]
              : []),
            ...(fuelSwitchingPropane > 0
              ? [
                  {
                    fuelSource: FuelSource.Propane,
                    carbonImpact: carbonImpactPropaneFuelSwitching,
                  },
                ]
              : []),
            ...(fuelSwitchingFuelOil > 0
              ? [
                  {
                    fuelSource: FuelSource.FuelOil,
                    carbonImpact: carbonImpactFuelOilFuelSwitching,
                  },
                ]
              : []),
            ...(fuelSwitchingDiesel > 0
              ? [
                  {
                    fuelSource: FuelSource.Diesel,
                    carbonImpact: carbonImpactDieselFuelSwitching,
                  },
                ]
              : []),
            ...(fuelSwitchingGasoline > 0
              ? [
                  {
                    fuelSource: FuelSource.Gasoline,
                    carbonImpact: carbonImpactGasolineFuelSwitching,
                  },
                ]
              : []),
          ],
        }
      : null;

  return buyCleanComponent;
};
